// src/features/admin/BenchmarksPanel.jsx
//
// ADMIN-BENCHMARKS-S1: Editable banded-metric thresholds panel.
//
// Displays and edits the "benchmark bands" for a selected metric.
// The first (and currently only) metric is Assignment % (assignment_pct).
//
// Each band has:
//   - Label   — free-text description
//   - Color   — green / amber / red
//   - Range   — derived display-only string (≤ 40, 41–69, …, > 100)
//
// Row actions: delete, move up / move down.
// Footer actions: Add band, Reset to defaults, Save.
// Non-admins see the table read-only (no Save/Add/Delete/Reset).
//
// Validation is run client-side before submitting; server errors are
// surfaced inline via an ErrorBanner below the table.

import React, { useCallback, useEffect, useState } from "react";
import { T } from "../../app/tokens.js";
import { S } from "../../app/styles.js";
import { ErrorBanner } from "../../components/ErrorBanner.jsx";
import {
  getBenchmarkBands,
  putBenchmarkBands,
  resetBenchmarkBands,
} from "../../data/benchmarkBandsClient.js";
import {
  getSpaceFitKnobs,
  putSpaceFitKnobs,
} from "../../data/spaceFitKnobsClient.js";
import { AuthError, ValidationError } from "../../data/errors.js";
import { log } from "../../utils/logger.js";

// ─── Style constants ──────────────────────────────────────────────────────────

const TITLE = {
  margin: 0,
  fontSize: T.fsLg5,
  fontWeight: T.fwSemibold,
  color: T.dark,
  letterSpacing: -0.2,
};

const SUBTITLE = {
  fontSize: T.fsBase,
  color: T.mid,
  marginTop: 6,
  marginBottom: 18,
  lineHeight: 1.5,
};

const TABLE_WRAPPER = {
  border: `0.5px solid ${T.border}`,
  borderRadius: 6,
  overflow: "hidden",
  marginBottom: 16,
};

const TH = {
  textAlign: "left",
  padding: "10px 14px",
  fontSize: T.fsXxs,
  fontWeight: T.fwSemibold,
  letterSpacing: "0.06em",
  textTransform: "uppercase",
  color: T.mid,
  background: T.lightGray,
  borderBottom: `0.5px solid ${T.border}`,
};

const TD = {
  padding: "10px 14px",
  fontSize: T.fsBase,
  color: T.dark,
  borderBottom: `0.5px solid ${T.border}`,
  verticalAlign: "middle",
};

const INPUT = {
  width: "100%",
  padding: "5px 8px",
  fontSize: T.fsBase,
  border: `1px solid ${T.border}`,
  borderRadius: 4,
  fontFamily: "inherit",
  color: T.dark,
  background: T.white,
};

const COLOR_DOT_SIZE = 14;

const FOOTER = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  marginTop: 8,
};

const BTN_PRIMARY = {
  background: T.blue,
  color: T.white,
  border: "none",
  borderRadius: 6,
  fontSize: T.fsSm,
  fontWeight: T.fwBold,
  padding: "6px 16px",
  cursor: "pointer",
};

const BTN_SECONDARY = {
  background: T.white,
  color: T.blue,
  border: `1px solid ${T.border}`,
  borderRadius: 6,
  fontSize: T.fsSm,
  fontWeight: T.fwSemibold,
  padding: "6px 14px",
  cursor: "pointer",
};

const BTN_DANGER = {
  background: T.white,
  color: T.red,
  border: `1px solid ${T.border}`,
  borderRadius: 6,
  fontSize: T.fsSm,
  fontWeight: T.fwSemibold,
  padding: "6px 14px",
  cursor: "pointer",
  marginLeft: "auto",
};

const BTN_ICON = {
  background: "transparent",
  color: T.mid,
  border: "none",
  borderRadius: 4,
  cursor: "pointer",
  padding: "2px 5px",
  fontSize: T.fsMd,
  lineHeight: 1,
};

const BTN_DELETE = {
  ...BTN_ICON,
  color: T.red,
};

const SELECT = {
  padding: "5px 8px",
  fontSize: T.fsBase,
  border: `1px solid ${T.border}`,
  borderRadius: 4,
  fontFamily: "inherit",
  color: T.dark,
  background: T.white,
};

const METRIC_OPTIONS = [
  { value: "assignment_pct",  label: "Assignment %" },
  { value: "space_fit_score", label: "Space Fit" },
];

const KNOB_SECTION = {
  background: T.lightGray,
  border: `0.5px solid ${T.border}`,
  borderRadius: 6,
  padding: "14px 16px",
  marginBottom: 16,
};

const KNOB_CAPTION = {
  fontSize: T.fsXs,
  color: T.mid,
  marginTop: 8,
  marginBottom: 0,
  lineHeight: 1.5,
};

const KNOB_ROW = {
  display: "flex",
  alignItems: "center",
  gap: 16,
  flexWrap: "wrap",
  marginBottom: 12,
};

const KNOB_FIELD = {
  display: "flex",
  flexDirection: "column",
  gap: 4,
};

const KNOB_LABEL = {
  fontSize: T.fsXs,
  fontWeight: T.fwSemibold,
  color: T.mid,
  letterSpacing: "0.04em",
  textTransform: "uppercase",
};

const KNOB_INPUT = {
  width: 90,
  padding: "5px 8px",
  fontSize: T.fsBase,
  border: `1px solid ${T.border}`,
  borderRadius: 4,
  fontFamily: "inherit",
  color: T.dark,
  background: T.white,
};

const INLINE_MSG = {
  fontSize: T.fsXs,
  marginLeft: 8,
};

const COLOR_OPTIONS = ["green", "amber", "red"];

const COLOR_LABELS = { green: "Green", amber: "Amber", red: "Red" };

const COLOR_HEX = {
  green: T.green,
  amber: T.amber,
  red:   T.red,
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

function makeDot(color) {
  return (
    <span
      style={{
        display: "inline-block",
        width: COLOR_DOT_SIZE,
        height: COLOR_DOT_SIZE,
        borderRadius: "50%",
        background: COLOR_HEX[color] ?? T.light,
        flexShrink: 0,
        border: `1px solid ${T.border}`,
      }}
    />
  );
}

/**
 * Derive the human-readable range string for a band given its position.
 * sorted: array of bands sorted by band_order ascending, as edited locally.
 * idx: index into sorted.
 */
function deriveRange(sorted, idx) {
  const band = sorted[idx];

  // Lower bound is the previous band's max_value + 1 (or 0 if first band)
  let lo = null;
  if (idx > 0) {
    const prevMax = sorted[idx - 1].max_value;
    if (prevMax === null || prevMax === undefined || prevMax === "") {
      lo = null; // previous was open-ended — shouldn't happen in valid set
    } else {
      lo = Number(prevMax) + 1;
    }
  }

  const maxV = band.max_value;
  const isOpen = maxV === null || maxV === undefined || maxV === "";

  if (idx === 0 && isOpen) return "All values";
  if (idx === 0) return `≤ ${maxV}`;
  if (isOpen) return lo != null ? `> ${lo - 1}` : "All remaining";
  if (lo != null) {
    return lo === Number(maxV) ? `${lo}` : `${lo}-${maxV}`;
  }
  return `≤ ${maxV}`;
}

/**
 * Client-side validation mirrors server-side validateBandSet.
 * Returns an error string or null.
 */
function validateLocalBands(bands) {
  if (bands.length === 0) return "Add at least one band.";

  const orders = bands.map((_, i) => i); // after reorder, indices are canonical band_order

  const nullBands = bands.filter(
    (b) => b.max_value === null || b.max_value === undefined || b.max_value === "",
  );

  if (nullBands.length === 0) return "The last band must have no upper limit (leave max_value blank).";
  if (nullBands.length > 1)  return "Only the last band may have no upper limit.";
  if (bands.indexOf(nullBands[0]) !== bands.length - 1) {
    return "The open-ended band (no upper limit) must be the last row.";
  }

  const cappedBands = bands.slice(0, -1);
  for (const b of cappedBands) {
    const n = Number(b.max_value);
    if (isNaN(n) || n < 0) return `"${b.label || "(unlabelled)"}" has an invalid upper limit.`;
  }

  for (let i = 1; i < cappedBands.length; i++) {
    const prev = Number(cappedBands[i - 1].max_value);
    const curr = Number(cappedBands[i].max_value);
    if (curr <= prev) {
      return `Upper limits must be strictly increasing. "${cappedBands[i].label || "(unlabelled)"}" (${curr}) is not greater than the row above (${prev}).`;
    }
  }

  for (const b of bands) {
    if (!b.color || !COLOR_OPTIONS.includes(b.color)) {
      return `Invalid color "${b.color}". Choose green, amber, or red.`;
    }
    if (!b.label || b.label.trim() === "") return "Every band must have a label.";
  }

  return null;
}

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Validate Space Fit knob values client-side.
 * Returns an error string or null.
 */
function validateKnobs(avgTarget, peakComfort, peakCeiling, overSeatedFloor, minHeadroomPct) {
  // Fields with range 1–99 (avoid zero-denominator; must be meaningful percentages).
  for (const [name, val] of [
    ["Avg target",              avgTarget],
    ["Over-seated floor",       overSeatedFloor],
    ["Min densification capacity", minHeadroomPct],
  ]) {
    const n = Number(val);
    if (val === "" || val === null || val === undefined || !Number.isFinite(n) || n < 1 || n > 99) {
      return `${name} must be a number between 1 and 99.`;
    }
  }
  // Peak comfort / ceiling: 0–100.
  for (const [name, val] of [
    ["Peak comfort", peakComfort],
    ["Peak ceiling", peakCeiling],
  ]) {
    const n = Number(val);
    if (val === "" || val === null || val === undefined || !Number.isFinite(n) || n < 0 || n > 100) {
      return `${name} must be a number between 0 and 100.`;
    }
  }
  if (Number(peakComfort) >= Number(peakCeiling)) {
    return "Peak comfort must be less than peak ceiling.";
  }
  return null;
}

export function BenchmarksPanel({ currentRole } = {}) {
  const isAdmin = currentRole === "admin";

  const [metricKey, setMetricKey] = useState("assignment_pct");
  const [bands,     setBands]     = useState(null);  // null = loading
  const [error,     setError]     = useState(null);
  const [saveError, setSaveError] = useState(null);
  const [busy,      setBusy]      = useState(false);
  const [reloads,   setReloads]   = useState(0);

  // Space Fit knob state
  const [knobs,      setKnobs]      = useState({ avgTarget: "", peakComfort: "", peakCeiling: "", overSeatedFloor: "", minHeadroomPct: "" });
  const [knobsLoaded, setKnobsLoaded] = useState(false);
  const [knobBusy,  setKnobBusy]   = useState(false);
  const [knobMsg,   setKnobMsg]    = useState(null); // { ok: bool, text: string } | null

  // Fetch knobs when Space Fit is selected
  useEffect(() => {
    if (metricKey !== "space_fit_score") return;
    let cancelled = false;
    setKnobsLoaded(false);
    setKnobMsg(null);
    (async () => {
      try {
        const out = await getSpaceFitKnobs();
        if (cancelled) return;
        setKnobs({
          avgTarget:       String(out.avgTarget),
          peakComfort:     String(out.peakComfort),
          peakCeiling:     String(out.peakCeiling),
          overSeatedFloor: String(out.overSeatedFloor ?? 45),
          minHeadroomPct:  String(out.minHeadroomPct  ?? 10),
        });
        setKnobsLoaded(true);
      } catch (err) {
        if (cancelled) return;
        log.error("BenchmarksPanel: knobs load failed", { err: String(err) });
        setKnobs({ avgTarget: "65", peakComfort: "75", peakCeiling: "90", overSeatedFloor: "45", minHeadroomPct: "10" });
        setKnobsLoaded(true);
      }
    })();
    return () => { cancelled = true; };
  }, [metricKey]);

  // Fetch bands when metric changes or after reset
  useEffect(() => {
    let cancelled = false;
    setError(null);
    setBands(null);
    (async () => {
      try {
        const out = await getBenchmarkBands(metricKey);
        if (cancelled) return;
        // Transform to local shape: max_value as string for the input (empty = open)
        const local = (out?.bands ?? []).map((b, i) => ({
          _key:      b.id ?? `new-${i}`,
          label:     b.label ?? "",
          color:     b.color ?? "green",
          max_value: b.maxValue !== null && b.maxValue !== undefined ? String(b.maxValue) : "",
        }));
        setBands(local);
      } catch (err) {
        if (cancelled) return;
        log.error("BenchmarksPanel: load failed", { err: String(err) });
        setError(err);
        setBands([]);
      }
    })();
    return () => { cancelled = true; };
  }, [metricKey, reloads]);

  // ── Row mutators ──────────────────────────────────────────────────────────

  const updateBand = useCallback((idx, field, value) => {
    setBands((prev) => {
      if (!prev) return prev;
      const next = [...prev];
      next[idx] = { ...next[idx], [field]: value };
      return next;
    });
    setSaveError(null);
  }, []);

  const deleteBand = useCallback((idx) => {
    setBands((prev) => {
      if (!prev) return prev;
      return prev.filter((_, i) => i !== idx);
    });
    setSaveError(null);
  }, []);

  const moveUp = useCallback((idx) => {
    if (idx === 0) return;
    setBands((prev) => {
      if (!prev) return prev;
      const next = [...prev];
      [next[idx - 1], next[idx]] = [next[idx], next[idx - 1]];
      return next;
    });
    setSaveError(null);
  }, []);

  const moveDown = useCallback((idx) => {
    setBands((prev) => {
      if (!prev || idx >= prev.length - 1) return prev;
      const next = [...prev];
      [next[idx], next[idx + 1]] = [next[idx + 1], next[idx]];
      return next;
    });
    setSaveError(null);
  }, []);

  const addBand = useCallback(() => {
    setBands((prev) => {
      if (!prev) return prev;
      // Insert before the last (open-ended) band if one exists, else append
      const newBand = {
        _key:      `new-${Date.now()}`,
        label:     "",
        color:     "green",
        max_value: "",
      };
      const lastNull = prev.length > 0 &&
        (prev[prev.length - 1].max_value === "" ||
         prev[prev.length - 1].max_value === null);
      if (lastNull) {
        return [...prev.slice(0, -1), newBand, prev[prev.length - 1]];
      }
      return [...prev, newBand];
    });
    setSaveError(null);
  }, []);

  // ── Save knobs (Space Fit) ────────────────────────────────────────────────

  async function onSaveKnobs() {
    if (knobBusy) return;
    setKnobMsg(null);

    const validationErr = validateKnobs(
      knobs.avgTarget, knobs.peakComfort, knobs.peakCeiling,
      knobs.overSeatedFloor, knobs.minHeadroomPct,
    );
    if (validationErr) {
      setKnobMsg({ ok: false, text: validationErr });
      return;
    }

    setKnobBusy(true);
    try {
      const out = await putSpaceFitKnobs({
        avgTarget:       Number(knobs.avgTarget),
        peakComfort:     Number(knobs.peakComfort),
        peakCeiling:     Number(knobs.peakCeiling),
        overSeatedFloor: Number(knobs.overSeatedFloor),
        minHeadroomPct:  Number(knobs.minHeadroomPct),
      });
      setKnobs({
        avgTarget:       String(out.avgTarget),
        peakComfort:     String(out.peakComfort),
        peakCeiling:     String(out.peakCeiling),
        overSeatedFloor: String(out.overSeatedFloor ?? 45),
        minHeadroomPct:  String(out.minHeadroomPct  ?? 10),
      });
      setKnobMsg({ ok: true, text: "Knobs saved." });
    } catch (err) {
      if (err instanceof AuthError) {
        setKnobMsg({ ok: false, text: "You don't have permission to save knobs." });
      } else if (err instanceof ValidationError) {
        setKnobMsg({ ok: false, text: err.message });
      } else {
        log.error("BenchmarksPanel: knob save failed", { err: String(err) });
        setKnobMsg({ ok: false, text: "Something went wrong saving knobs. Please try again." });
      }
    } finally {
      setKnobBusy(false);
    }
  }

  // ── Save ─────────────────────────────────────────────────────────────────

  async function onSave() {
    if (busy || !bands) return;
    setSaveError(null);

    const validationError = validateLocalBands(bands);
    if (validationError) {
      setSaveError(new ValidationError(validationError));
      return;
    }

    // Convert local shape → server shape
    const payload = bands.map((b, i) => ({
      band_order: i,
      max_value:  b.max_value === "" ? null : Number(b.max_value),
      color:      b.color,
      label:      b.label.trim(),
    }));

    setBusy(true);
    try {
      const out = await putBenchmarkBands(metricKey, payload);
      const local = (out?.bands ?? []).map((b, i) => ({
        _key:      b.id ?? `saved-${i}`,
        label:     b.label ?? "",
        color:     b.color ?? "green",
        max_value: b.maxValue !== null && b.maxValue !== undefined ? String(b.maxValue) : "",
      }));
      setBands(local);
    } catch (err) {
      if (err instanceof AuthError) {
        setSaveError(new Error("You don't have permission to save benchmark bands."));
      } else if (err instanceof ValidationError) {
        setSaveError(err);
      } else {
        log.error("BenchmarksPanel: save failed", { err: String(err) });
        setSaveError(new Error("Something went wrong saving. Please try again."));
      }
    } finally {
      setBusy(false);
    }
  }

  // ── Reset ─────────────────────────────────────────────────────────────────

  async function onReset() {
    if (busy) return;
    setSaveError(null);
    setBusy(true);
    try {
      await resetBenchmarkBands(metricKey);
      setReloads((n) => n + 1);
    } catch (err) {
      if (err instanceof AuthError) {
        setSaveError(new Error("You don't have permission to reset benchmark bands."));
      } else {
        log.error("BenchmarksPanel: reset failed", { err: String(err) });
        setSaveError(new Error("Something went wrong resetting. Please try again."));
      }
    } finally {
      setBusy(false);
    }
  }

  // ── Render ────────────────────────────────────────────────────────────────

  const loading = bands === null;
  const canEdit = isAdmin && !busy;

  return (
    <div>
      {/* Header */}
      <p style={TITLE}>Benchmarks</p>
      <p style={SUBTITLE}>
        Define colored threshold bands that classify a metric value as good, warning, or bad.
        {metricKey === "assignment_pct" && " These bands color-code the Assignment % bar across the portfolio."}
        {metricKey === "space_fit_score" && " Configure the Space Fit formula and score-to-color mapping."}
      </p>

      {/* Metric selector */}
      <div style={{ marginBottom: 18, display: "flex", alignItems: "center", gap: 12 }}>
        <label
          htmlFor="benchmark-metric-select"
          style={{ fontSize: T.fsBase, fontWeight: T.fwSemibold, color: T.dark }}
        >
          Metric
        </label>
        <select
          id="benchmark-metric-select"
          data-testid="benchmark-metric-select"
          style={SELECT}
          value={metricKey}
          onChange={(e) => { setMetricKey(e.target.value); setSaveError(null); }}
          disabled={busy}
        >
          {METRIC_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>
      </div>

      {/* Space Fit knob editor */}
      {metricKey === "space_fit_score" && knobsLoaded && (
        <div style={KNOB_SECTION} data-testid="space-fit-knobs-section">
          {/* Row 1: Over-seated floor + Min densification headroom */}
          <div style={KNOB_ROW}>
            <div style={KNOB_FIELD}>
              <label htmlFor="knob-over-seated-floor" style={KNOB_LABEL}>Over-seated floor %</label>
              <input
                id="knob-over-seated-floor"
                data-testid="knob-over-seated-floor"
                type="number"
                min="1"
                max="99"
                step="1"
                style={KNOB_INPUT}
                value={knobs.overSeatedFloor}
                disabled={!isAdmin || knobBusy}
                onChange={(e) => { setKnobs((k) => ({ ...k, overSeatedFloor: e.target.value })); setKnobMsg(null); }}
              />
            </div>
            <div style={KNOB_FIELD}>
              <label htmlFor="knob-min-headroom-pct" style={KNOB_LABEL}>Min densification capacity %</label>
              <input
                id="knob-min-headroom-pct"
                data-testid="knob-min-headroom-pct"
                type="number"
                min="1"
                max="99"
                step="1"
                style={KNOB_INPUT}
                value={knobs.minHeadroomPct}
                disabled={!isAdmin || knobBusy}
                onChange={(e) => { setKnobs((k) => ({ ...k, minHeadroomPct: e.target.value })); setKnobMsg(null); }}
              />
            </div>
          </div>
          <p style={{ ...KNOB_CAPTION, marginBottom: 12 }}>
            <strong>Over-seated floor:</strong> Sites where average utilization stays below this % are flagged Over-seated (amber).
            Raise it to widen the Over-seated band; lower it to narrow it.{" "}
            <strong>Min densification capacity:</strong> A site must have at least this % of its installed desk count available as densification capacity to qualify as Expandable. Below this threshold it is classified Tight instead.
          </p>
          {/* Row 2: Score formula knobs */}
          <div style={KNOB_ROW}>
            <div style={KNOB_FIELD}>
              <label htmlFor="knob-avg-target" style={KNOB_LABEL}>Avg target %</label>
              <input
                id="knob-avg-target"
                data-testid="knob-avg-target"
                type="number"
                min="1"
                max="99"
                step="1"
                style={KNOB_INPUT}
                value={knobs.avgTarget}
                disabled={!isAdmin || knobBusy}
                onChange={(e) => { setKnobs((k) => ({ ...k, avgTarget: e.target.value })); setKnobMsg(null); }}
              />
            </div>
            <div style={KNOB_FIELD}>
              <label htmlFor="knob-peak-comfort" style={KNOB_LABEL}>Comfort level - room to spare below this peak %</label>
              <input
                id="knob-peak-comfort"
                data-testid="knob-peak-comfort"
                type="number"
                min="0"
                max="100"
                step="1"
                style={KNOB_INPUT}
                value={knobs.peakComfort}
                disabled={!isAdmin || knobBusy}
                onChange={(e) => { setKnobs((k) => ({ ...k, peakComfort: e.target.value })); setKnobMsg(null); }}
              />
            </div>
            <div style={KNOB_FIELD}>
              <label htmlFor="knob-peak-ceiling" style={KNOB_LABEL}>Crowding threshold - peak vs seats %</label>
              <input
                id="knob-peak-ceiling"
                data-testid="knob-peak-ceiling"
                type="number"
                min="0"
                max="100"
                step="1"
                style={KNOB_INPUT}
                value={knobs.peakCeiling}
                disabled={!isAdmin || knobBusy}
                onChange={(e) => { setKnobs((k) => ({ ...k, peakCeiling: e.target.value })); setKnobMsg(null); }}
              />
            </div>
            {isAdmin && (
              <div style={{ alignSelf: "flex-end", display: "flex", alignItems: "center", gap: 8 }}>
                <button
                  type="button"
                  data-testid="knob-save-btn"
                  style={{ ...BTN_PRIMARY, marginTop: 0 }}
                  disabled={knobBusy}
                  onClick={onSaveKnobs}
                >
                  {knobBusy ? "Saving…" : "Save knobs"}
                </button>
                {knobMsg && (
                  <span
                    data-testid="knob-save-msg"
                    style={{ ...INLINE_MSG, color: knobMsg.ok ? T.green : T.red }}
                  >
                    {knobMsg.text}
                  </span>
                )}
              </div>
            )}
          </div>
          <p style={KNOB_CAPTION}>
            Score = the worse of usage vs capacity; color from the score band.
            Usage measures how well avg utilization meets the target;
            capacity measures peak clearance before the ceiling.
          </p>
        </div>
      )}

      {/* Fetch error */}
      {error && (
        <ErrorBanner error={error} style={{ marginBottom: 16 }} />
      )}

      {/* Table */}
      {loading && !error && (
        <p style={{ color: T.mid, fontSize: T.fsBase }}>Loading bands…</p>
      )}

      {!loading && (
        <div style={TABLE_WRAPPER}>
          <table
            data-testid="benchmark-bands-table"
            style={{ width: "100%", borderCollapse: "collapse" }}
          >
            <thead>
              <tr>
                <th style={TH}>Label</th>
                <th style={TH}>Color</th>
                <th style={TH}>Upper limit</th>
                <th style={TH}>Range</th>
                {canEdit && <th style={{ ...TH, textAlign: "right" }}>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {bands.length === 0 && (
                <tr>
                  <td
                    colSpan={canEdit ? 5 : 4}
                    style={{ ...TD, color: T.mid, textAlign: "center", padding: "20px 14px" }}
                  >
                    No bands configured. Click "Add band" to get started.
                  </td>
                </tr>
              )}
              {bands.map((band, idx) => {
                const isLast = idx === bands.length - 1;
                const rangeStr = deriveRange(bands, idx);
                const isLastRow = idx === bands.length - 1;

                return (
                  <tr key={band._key} data-testid={`band-row-${idx}`}>
                    {/* Label */}
                    <td style={TD}>
                      {canEdit ? (
                        <input
                          data-testid={`band-label-${idx}`}
                          style={INPUT}
                          value={band.label}
                          placeholder="e.g. On target"
                          onChange={(e) => updateBand(idx, "label", e.target.value)}
                        />
                      ) : (
                        band.label || <span style={{ color: T.light }}>—</span>
                      )}
                    </td>

                    {/* Color */}
                    <td style={{ ...TD, minWidth: 120 }}>
                      {canEdit ? (
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          {makeDot(band.color)}
                          <select
                            data-testid={`band-color-${idx}`}
                            style={SELECT}
                            value={band.color}
                            onChange={(e) => updateBand(idx, "color", e.target.value)}
                          >
                            {COLOR_OPTIONS.map((c) => (
                              <option key={c} value={c}>{COLOR_LABELS[c]}</option>
                            ))}
                          </select>
                        </div>
                      ) : (
                        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                          {makeDot(band.color)}
                          <span>{COLOR_LABELS[band.color] ?? band.color}</span>
                        </div>
                      )}
                    </td>

                    {/* Upper limit */}
                    <td style={{ ...TD, minWidth: 110 }}>
                      {canEdit ? (
                        isLast ? (
                          <span style={{ color: T.light, fontStyle: "italic" }}>None (open)</span>
                        ) : (
                          <input
                            data-testid={`band-max-${idx}`}
                            style={{ ...INPUT, width: 80 }}
                            type="number"
                            min="0"
                            step="1"
                            value={band.max_value}
                            placeholder="e.g. 70"
                            onChange={(e) => updateBand(idx, "max_value", e.target.value)}
                          />
                        )
                      ) : (
                        band.max_value === "" || band.max_value === null
                          ? <span style={{ color: T.light, fontStyle: "italic" }}>None (open)</span>
                          : band.max_value
                      )}
                    </td>

                    {/* Range (derived, read-only) */}
                    <td style={{ ...TD, color: T.mid, fontVariantNumeric: "tabular-nums" }}>
                      {rangeStr}
                    </td>

                    {/* Row actions */}
                    {canEdit && (
                      <td style={{ ...TD, textAlign: "right", whiteSpace: "nowrap" }}>
                        <button
                          type="button"
                          data-testid={`band-move-up-${idx}`}
                          style={BTN_ICON}
                          title="Move up"
                          disabled={idx === 0}
                          onClick={() => moveUp(idx)}
                        >
                          ↑
                        </button>
                        <button
                          type="button"
                          data-testid={`band-move-down-${idx}`}
                          style={BTN_ICON}
                          title="Move down"
                          disabled={isLastRow}
                          onClick={() => moveDown(idx)}
                        >
                          ↓
                        </button>
                        <button
                          type="button"
                          data-testid={`band-delete-${idx}`}
                          style={{ ...BTN_DELETE, marginLeft: 6 }}
                          title="Delete band"
                          onClick={() => deleteBand(idx)}
                        >
                          ×
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Save error */}
      {saveError && (
        <ErrorBanner error={saveError} style={{ marginBottom: 12 }} />
      )}

      {/* Footer */}
      {!loading && isAdmin && (
        <div style={FOOTER}>
          <button
            type="button"
            data-testid="benchmark-add-band"
            style={BTN_SECONDARY}
            onClick={addBand}
            disabled={busy}
          >
            + Add band
          </button>
          <button
            type="button"
            data-testid="benchmark-save"
            style={BTN_PRIMARY}
            onClick={onSave}
            disabled={busy}
          >
            {busy ? "Saving…" : "Save"}
          </button>
          <button
            type="button"
            data-testid="benchmark-reset"
            style={BTN_DANGER}
            onClick={onReset}
            disabled={busy}
          >
            Reset to defaults
          </button>
        </div>
      )}
    </div>
  );
}
